import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaTrash } from "react-icons/fa";
import BaseButton from "../../Components/Base/BaseButton";
import {
  listJobCards,
  deleteJobCard,
  updateCardStatus,
} from "../../Api/jobcard";
import Loader from "Components/Base/BaseLoader";
import DeleteModal from "../../Components/Base/DeleteModal";
import { toast } from "react-toastify";
import BaseModal from "Components/Base/BaseModal";
import { RiSearchLine } from "react-icons/ri";
import { getPriorityColor, getStatusColor } from "Components/constants/JobCard";
import { cancelLabel, DeleteLabel, editLabel, submitLabel } from "Components/constants/Role";
import Pagination from "Components/Common/Pagination";
import { renderSkeletons } from "Components/constants/layout";
interface JobCard {
  id: string;
  reported_issue: string;
  job_description: string;
  technician: { technician_name: string }[];
  priority_level: string;
  status: string;
  warranty_start_date: string;
  warranty_end_date: string;
  completion_date: string;
  jobCardProduct: {
    id: string;
    product: { product_code: string };
    quantity: number;
  }[];
  authUser: {
    id: string;
    name: string;
  };
  start_date: string;
}

const JobCardPage = () => {

  document.title = "Job Card"
  const navigate = useNavigate();
  const [jobCards, setJobCards] = useState<JobCard[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [selectedJobCardId, setSelectedJobCardId] = useState<string | null>(
    null
  );
  const [btnLoader, setBtnLoader] = useState<boolean>(false);
  const [showStatusModal, setShowStatusModal] = useState<boolean>(false);
  const [selectedJobCard, setSelectedJobCard] = useState<JobCard | null>(null);
  const [currentStatus, setCurrentStatus] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit, setlimit] = useState(12);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [onValueSearch, setOnValueSearch] = useState<string>("");
  const fetchJobCards = async () => {
    const payload = {
      page: currentPage,
      limit: limit,
      search: onValueSearch,
    };
    try {
      setLoading(true);
      const response = await listJobCards(payload);
      if (response && response.data && response.data.jobCards) {
        setJobCards(response.data.jobCards);
        setTotalRecords(response.data.totalItems);
        setTotalPages(response.data.totalPages);
        setCurrentPage(response.data.currentPage);
      } else {
        setError("No job cards found.");
      }
    } catch (error) {
      console.error("Error fetching job cards: ", error);
      // setError("Failed to load job cards.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobCards();
  }, [currentPage, limit, onValueSearch, totalPages, totalRecords]);

  const handleDeleteClick = (jobCardId: string) => {
    setSelectedJobCardId(jobCardId);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!selectedJobCardId) return;
    setBtnLoader(true);

    try {
      const response = await deleteJobCard(selectedJobCardId);
      if (response) {
        setJobCards((prevJobCards) =>
          prevJobCards.filter((jobCard) => jobCard.id !== selectedJobCardId)
        );
        toast.success("Job card deleted successfully!");
      } else {
        console.error("Deletion failed: ", response.message);
        toast.error("Failed to delete job card.");
      }
    } catch (error) {
      console.error("Error deleting job card: ", error);
      toast.error("Error deleting job card.");
    } finally {
      setBtnLoader(false);
      setShowDeleteModal(false);
      setSelectedJobCardId(null);
    }
  };
  const handleStatusUpdateClick = (job: JobCard) => {
    setSelectedJobCard(job);
    setCurrentStatus(job.status);
    setShowStatusModal(true);
  };

  const truncateText = (text: string, wordLimit: number) => {
    const words = text.split(" ");
    if (words.length <= wordLimit) return text;
    return words.slice(0, wordLimit).join(" ") + " ...";
  };

  const handleUpdateStatus = async (newStatus: string) => {
    if (selectedJobCard) {
      const payload = { status: newStatus };
      setBtnLoader(true);
      try {
        await updateCardStatus(selectedJobCard.id, payload);
        toast.success("Job card status updated successfully!");
        setJobCards((prevJobCards) =>
          prevJobCards.map((jobCard) =>
            jobCard.id === selectedJobCard.id
              ? { ...jobCard, status: newStatus }
              : jobCard
          )
        );
      } catch (error) {
        console.error("Error updating job card status: ", error);
        toast.error("Failed to update job card status.");
      } finally {
        setBtnLoader(false);
        setShowStatusModal(false);
        setSelectedJobCard(null);
      }
    }
  };

  // if (error) return <p>{error}</p>;

  return (
    <div className="page-content mb-2">
      <div className="mb-1  d-flex justify-content-between align-items-center">
        <span className="fs-4 d-flex gap-4">Job Cards</span>
        <div className="d-flex align-items-center gap-2">
          <BaseButton
            className="btn btn-success"
            onClick={() => navigate("/add-job")}
            children={
              <>
                <i className="mdi mdi-plus" />
                Add Job
              </>
            }
          />
        </div>
      </div>

      <Pagination
        pageSizeValue={limit}
        totalPages={totalPages}
        totalRecords={totalRecords}
        setOnValueSearchBar={setOnValueSearch}
        data={jobCards}
        setPageSizeValue={setlimit}
        setCurrentPageValue={setCurrentPage}
        children={
          <>
            {loading ? (
              <div className="d-flex row mt-3">{renderSkeletons(limit)}</div>
            ) : (
              <div className="container-fluid m-0 p-0">
                {jobCards.length > 0 ? (
                  <div className="row g-3">
                    {jobCards.map((job) => (
                      <div key={job.id} className="col-12 col-md-6 col-lg-4">
                        <div className="card shadow-sm">
                          <div className="card-body">
                            <div className="d-flex justify-content-between">
                              <div>
                                <p
                                  className={`p-1 ${getPriorityColor(
                                    job.priority_level
                                  )} rounded-pill`}
                                  style={{
                                    fontSize: "0.6rem",
                                    width: "fit-content",
                                  }}
                                >
                                  {job.priority_level}
                                </p>
                                <h5 className="card-title">
                                  {truncateText(job.reported_issue, 10)}
                                </h5>
                              </div>
                              <div
                                className={`p-2 rounded ${getStatusColor(
                                  job.status
                                )}`}
                                style={{
                                  fontSize: "0.6rem",
                                  height: "fit-content",
                                }}
                              >
                                {job.status}
                              </div>
                            </div>
                            <hr />
                            <div className="mb-3">
                              <p>
                                <strong>Job Id:</strong>{" "}
                                J{job.id}
                              </p>
                            </div>
                            <div className="mb-3">
                              <p>
                                <strong>Job Description:</strong>{" "}
                                {truncateText(job.job_description, 10)}
                              </p>
                            </div>
                            <div className="mb-2">
                              <p>
                                {" "}
                                <strong>Product Code:</strong>{" "}
                                {job.jobCardProduct
                                  .map((item) => item.product.product_code)
                                  .join(", ")}
                              </p>
                              <p>
                                <strong>Warranty Start Date:</strong>{" "}
                                {job.warranty_start_date}
                              </p>
                              <p>
                                <strong>Quantity:</strong>{" "}
                                {job.jobCardProduct[0]?.quantity}
                              </p>
                            </div>
                            <div className="mb-2">
                              <p>
                                <strong>Customer Name:</strong>{" "}
                                {job.authUser.name}
                              </p>
                              <p>
                                <strong>Start Date:</strong> {job.start_date}
                              </p>
                              <p>
                                <strong>Completion Date:</strong>{" "}
                                {job.completion_date}
                              </p>
                            </div>
                            <hr />
                            <div className="d-flex justify-content-between">
                              <p>
                                <strong>Technician(s):</strong>{" "}
                                {job.technician
                                  .map((t) => t.technician_name)
                                  .join(", ")}
                              </p>
                             
                            </div>
                            <hr></hr>
                            <div className="d-flex justify-content-start gap-1">
                                <BaseButton
                                  className="btn btn-light py-0 px-2"
                                  onClick={() =>
                                    navigate(`/job-form/${job.id}`)
                                  }
                                >
                                  <span className="tooltip-container">
                                    <i className="mdi mdi-pencil-outline fs-4 text-dark cursor-pointer"></i>
                                    <span className="tooltip-text">
                                      {editLabel}
                                    </span>
                                  </span>
                                </BaseButton>
                                <BaseButton
                                  className="btn btn-light py-0 px-2"
                                  onClick={() => handleDeleteClick(job.id)}
                                >
                                  <span className="tooltip-container">
                                    <i className="mdi mdi-delete-outline fs-4 text-danger cursor-pointer"></i>
                                    <span className="tooltip-text">
                                      {DeleteLabel}
                                    </span>
                                  </span>
                                </BaseButton>
                                <BaseButton
                                  className="btn btn-light py-0 px-2"
                                  onClick={() => handleStatusUpdateClick(job)}
                                >
                                  <span className="tooltip-container">
                                    <i className="mdi mdi-update fs-4 text-success cursor-pointer"></i>
                                    <span className="tooltip-text">
                                      Status Update
                                    </span>
                                  </span>
                                </BaseButton>
                              </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-4 text-center">
                    <div>
                      <span className="fs-1 text-success">
                        <RiSearchLine />
                      </span>
                    </div>
                    <div className="mt-4">
                      <h5>No Job Card Found</h5>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        }
      />

      <DeleteModal
        btnLoader={btnLoader}
        onCloseClick={() => setShowDeleteModal(false)}
        onDeleteClick={handleDelete}
        show={showDeleteModal}
      />

      <BaseModal
        open={showStatusModal}
        toggle={() => setShowStatusModal(false)}
        title="Status"
        closeLabel={cancelLabel}
        submitLabel={submitLabel}
        closeLabelColor="danger"
        submitLabelColor="success"
        submit={() => handleUpdateStatus(currentStatus)}
        loader={btnLoader}
      
      >
        <select
          value={currentStatus}
          onChange={(e) => setCurrentStatus(e.target.value)}
          className="form-select"
        >
          {["In Progress", "Reopen", "Closed"].map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      </BaseModal>
    </div>
  );
};

export default JobCardPage;
